import { isoDate as melDate } from "@/lib/intl";
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, History } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WeeklyGrid } from "./WeeklyGrid";
import { WeekDetail } from "./WeekDetail";
import { ExportDialog } from "./ExportDialog";
import { errMsg } from "./kpiFormat";

const shift = (iso, weeks) => {
  const [y, m, d] = iso.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + 7 * weeks));
  return melDate(t);
};

export const WeeklyReportTab = ({ meta, initialWeek }) => {
  const [end, setEnd] = useState(meta.last_completed_week);
  const [grid, setGrid] = useState(null);
  const [selected, setSelected] = useState(initialWeek || meta.last_completed_week);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/kpi/reports", { params: { end, weeks: 8 } })
    .then((r) => setGrid(r.data)).catch((e) => toast.error(errMsg(e))), [end]);
  useEffect(() => { load(); }, [load]);

  const backfill = async () => {
    setBusy(true);
    try {
      const r = await api.post("/kpi/reports/backfill", { end, weeks: 8 }, { timeout: 300000 });
      toast.success(r.data.generated.length ? `${r.data.generated.length} draft(s) generated` : "Every finished week already has a report");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const defaultStart = meta.settings.dashboard_start_week || shift(meta.last_completed_week, -51);
  const missing = grid?.weeks.some((w) => w.status === "none" && w.finished);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" onClick={() => setEnd(shift(end, -8))} data-testid="kpi-grid-prev-btn" aria-label="Earlier weeks">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="outline" disabled={end >= meta.last_completed_week}
            onClick={() => setEnd(shift(end, 8) > meta.last_completed_week ? meta.last_completed_week : shift(end, 8))}
            data-testid="kpi-grid-next-btn" aria-label="Later weeks"><ChevronRight className="h-4 w-4" /></Button>
          <span className="text-xs text-zinc-500">8 weeks · click a column to review it</span>
        </div>
        <div className="flex items-center gap-2">
          {missing && (
            <Button size="sm" variant="outline" onClick={backfill} disabled={busy} data-testid="kpi-backfill-btn">
              <History className="h-4 w-4 mr-1.5" />{busy ? "Generating…" : "Generate missing drafts"}
            </Button>
          )}
          <ExportDialog defaultStart={defaultStart} />
        </div>
      </div>
      {grid ? <WeeklyGrid grid={grid} selected={selected} onSelect={setSelected} />
        : <div className="h-96 animate-pulse rounded-lg bg-zinc-100" />}
      <div className="flex items-center gap-4 text-xs text-zinc-500" data-testid="kpi-grid-legend">
        <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" />provisional — recommended rule pending sign-off or unconfirmed products</span>
        <span><span className="text-emerald-700 font-medium">green</span> / <span className="text-red-600 font-medium">red</span> = against target</span>
        <span>N/A = data missing (hover for the reason)</span>
      </div>
      {selected && <WeekDetail week={selected} kpis={meta.kpis} canApprove={meta.can_approve} onChanged={load} />}
    </div>
  );
};
